import { useId, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { applyTheme, nextTheme, readTheme, THEME_LABEL } from "../theme";

export function Field({
  label,
  error,
  hint,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string; hint?: ReactNode }) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-e` : hint ? `${id}-h` : undefined}
        {...props}
      />
      {hint && !error && (
        <span className="hint" id={`${id}-h`}>
          {hint}
        </span>
      )}
      {error && (
        <span className="error" id={`${id}-e`} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function BrandMark({ size = 30 }: { size?: number }) {
  return (
    <span className="brand-mark" style={{ width: size, height: size }} aria-hidden>
      <svg width={size * 0.62} height={size * 0.62} viewBox="0 0 24 24" fill="none">
        <path
          d="M2 4.5c3.5-1.6 6.6-1.6 10 .5v15c-3.4-2.1-6.5-2.1-10-.5zM22 4.5c-3.5-1.6-6.6-1.6-10 .5v15c3.4-2.1 6.5-2.1 10-.5z"
          fill="#F7F3EA"
        />
      </svg>
    </span>
  );
}

export function ThemeToggle() {
  const [theme, setTheme] = useState(readTheme);
  const label = THEME_LABEL[theme];
  return (
    <button
      type="button"
      className="icon-btn"
      title={label}
      aria-label={`${label}. Clique para alternar.`}
      onClick={() => {
        const n = nextTheme(theme);
        applyTheme(n);
        setTheme(n);
      }}
    >
      {theme === "dark" ? (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
      ) : theme === "light" ? (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      ) : (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 3v18" />
          <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" />
        </svg>
      )}
    </button>
  );
}

export function Spinner({ label = "Carregando" }: { label?: string }) {
  return (
    <span
      role="status"
      style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--ink-2)" }}
    >
      <span className="spinner" aria-hidden />
      {label}…
    </span>
  );
}
